import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Header, SpaceBetween, Box, ColumnLayout,
  KeyValuePairs, Spinner, Alert,
} from "@cloudscape-design/components";
import { UserInfo, SiteConfig } from "../types";
import { apiClient } from "../api/client";

interface Props { user: UserInfo; siteId: string; }

export const Information: React.FC<Props> = ({ user, siteId }) => {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiClient.get(`/sites?siteId=${siteId}`);
      setConfig(data.site || null);
    } catch (err: any) {
      setError(`Failed to load site configuration: ${err.message}`);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  if (loading) {
    return <Box textAlign="center" padding={{ top: "xxxl" }}><Spinner size="large" /></Box>;
  }

  if (error || !config) {
    return (
      <Alert type="warning" header="Site not configured">
        No configuration found for site {siteId}. Contact a superadmin to onboard this site.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h1">Information</Header>}>
        <ColumnLayout columns={2}>
          <KeyValuePairs
            items={[
              { label: "Weekly Call", value: config.callDay ? `${config.callDay}${config.callTime ? ' at ' + config.callTime : ''}` : "Not set" },
              { label: "Zoom ID", value: config.zoomId || "Not set" },
              { label: "Invite List", value: config.inviteList || "Not set" },
              { label: "Site", value: config.siteId },
            ]}
          />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2">Maintenance Information</Header>}>
        <Box variant="p">
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.9rem", lineHeight: 1.7, color: "#565959" }}>
            {config.tenets || "No maintenance information configured for this site."}
          </pre>
        </Box>
      </Container>
    </SpaceBetween>
  );
};
